import type React from 'react';
import { useState } from 'react';
import { Field } from 'react-final-form';
import { Box, FormField, FormHelp, Icon, IconButton, Input, Label } from '@zillow/constellation-10';
import { IconConcealFilled, IconRevealFilled } from '@zillow/constellation-icons';
import { IMaskMixin } from 'react-imask';
import { FieldValidator } from '../../../../validation/validation';
import { SSN } from '../../../../constants/formFieldNames';
import { useTermSet } from '../../../../IdvContext';
import { TermSet } from '../../../../../../models';

// The "size" and "as" props that come from the IMaskMixin have type definitions that are
// different than the "size" and "as" props that the Input component accept, so we don't
// want to spread those props into the Input component.
const MaskedSsnInput = IMaskMixin<HTMLInputElement>(({ inputRef, size: _size, as: _as, ...props }) => (
  <Input {...props} ref={inputRef} placeholder="XXX-XX-XXXX" autoComplete="off" />
));

export default function SocialSecurityNumberField() {
  const termSet = useTermSet();
  const [isSsnHidden, setIsSsnHidden] = useState(true);

  return (
    <Field
      name={SSN}
      render={({ input, meta }) => {
        const { touched, active, error } = meta;
        const isInvalid = touched && !active && Boolean(error);
        const { onChange, value: inputValue, ...restInput } = input;
        return (
          <FormField
            optional={termSet === TermSet.RenterScreening}
            label={<Label>{termSet === TermSet.RenterScreening ? 'SSN or ITIN' : 'Social Security number'}</Label>}
            error={isInvalid}
            control={
              <Box css={{ display: 'flex' }}>
                <Box css={{ marginRight: 'tighter', flexGrow: 1 }}>
                  <MaskedSsnInput
                    mask="000-00-0000"
                    displayChar={isSsnHidden ? '*' : null}
                    onAccept={(value: unknown) => onChange(value)}
                    value={inputValue}
                    {...restInput}
                  />
                </Box>
                <IconButton
                  title={isSsnHidden ? 'Show Social Security number' : 'Hide Social Security number'}
                  onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                    e.preventDefault();
                    setIsSsnHidden(!isSsnHidden);
                  }}
                >
                  <Icon>{isSsnHidden ? <IconConcealFilled /> : <IconRevealFilled />}</Icon>
                </IconButton>
              </Box>
            }
            description={
              isInvalid ? (
                <FormHelp key="error">{error}</FormHelp>
              ) : (
                <FormHelp key="help">
                  {termSet === TermSet.RenterScreening
                    ? 'Add your SSN or ITIN to accurately verify your identity and pull the correct screening reports'
                    : 'Leave blank if you do not have one'}
                </FormHelp>
              )
            }
          />
        );
      }}
      validate={(ssn: string) => {
        // Remove all non-numeric characters
        const sanitizedSsn = ssn?.replace(/\D/g, '');
        const lengthErrorText =
          termSet === TermSet.RenterScreening
            ? 'Enter in a 9-digit SSN or ITIN'
            : 'Social Security number must be exactly 9 digits';
        const errors = new FieldValidator(sanitizedSsn).length(9, lengthErrorText).build();

        return errors ? errors : null;
      }}
    />
  );
}
